<template>
  <BaseCard title="Archivos" :noHover="true" :collapsible="true">
    <div class="actions-internal">
      <button class="btn-action" @click="triggerUpload" :disabled="subiendo">
        {{ subiendo ? `Subiendo ${progreso}%...` : 'Subir Archivo' }}
      </button>
      <input
        ref="fileInput"
        id="archivo-paciente"
        name="archivo-paciente"
        type="file"
        style="display: none"
        @change="handleFile"
      />
    </div>

    <div v-if="subiendo" class="upload-progress">
      <div class="upload-progress-bar" :style="{ width: progreso + '%' }"></div>
    </div>

    <div v-if="errorSubida" class="upload-error">{{ errorSubida }}</div>

    <div v-if="archivos.length" class="files-list">
      <div v-for="(file, i) in archivos" :key="i" class="file-item">
        <img
          v-if="esImagen(file)"
          :src="file.url"
          alt="preview"
          class="file-preview"
          @click="abrirVisor(file)"
        />
        <div v-else class="file-icon-placeholder">📄</div>
        <div class="file-info">
          <span class="file-name">{{ file.nombre }}</span>
          <span class="file-type">{{ file.tipo }}</span>
          <span v-if="file.fechaSubida" class="file-fecha">{{ formatearFecha(file.fechaSubida) }}</span>
        </div>
        <div class="file-actions">
          <a :href="file.url" :download="file.nombre" class="btn-icon-action" title="Descargar">⬇️</a>
        </div>
      </div>
    </div>

    <p v-else class="empty-msg">No hay archivos adjuntos</p>
    <!-- Visor de Imágenes -->
    <div v-if="visorAbierto" class="visor-overlay" @click.self="cerrarVisor">
      <div class="visor-container">
        <button class="btn-cerrar" @click="cerrarVisor">×</button>
        
        <div 
          ref="visorWrapper"
          class="visor-imagen-wrapper" 
          :class="{ 'zoomed': zoomActivo }" 
          @click="handleZoomClick"
        >
          <img :src="imagenSeleccionada.url" :alt="imagenSeleccionada.nombre" />
        </div>

        <div class="visor-actions">
          <a :href="imagenSeleccionada.url" :download="imagenSeleccionada.nombre" class="btn-descargar-visor">
            Descargar
          </a>
        </div>
      </div>
    </div>
  </BaseCard>

  <ModalPagoPendiente :mostrar="mostrarModalPago" @cerrar="cerrarModalPago" />
</template>

<script setup>
import BaseCard from "./BaseCard.vue";
import ModalPagoPendiente from "./ModalPagoPendiente.vue";
import { ref, nextTick } from "vue";
import { firebaseStorage } from "@/firebase/storage";
import { usePagoVerificacion } from "@/composables/usePagoVerificacion";
import { ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";

const { mostrarModalPago, verificarPermisoPago, cerrarModalPago } = usePagoVerificacion();

const props = defineProps({
  archivos: { type: Array, default: () => [] },
  usuarioId: { type: String, required: true },
  pacienteId: { type: [String, Number], required: true },
});

const emit = defineEmits(["agregar"]);

const fileInput = ref(null);
const visorWrapper = ref(null);
const visorAbierto = ref(false);
const imagenSeleccionada = ref(null);
const zoomActivo = ref(false);

const subiendo = ref(false);
const progreso = ref(0);
const errorSubida = ref(null);

function triggerUpload() {
  if (!verificarPermisoPago()) return;
  if (fileInput.value) fileInput.value.click();
}

async function handleFile(event) {
  const file = event.target.files[0];
  if (!file) return;

  // Reset input para poder subir el mismo archivo de nuevo si hace falta
  event.target.value = "";

  subiendo.value = true;
  errorSubida.value = null;
  progreso.value = 0;

  const timestamp = Date.now();
  const path = `archivos/${props.usuarioId}/${props.pacienteId}/${timestamp}_${file.name}`;
  const fileRef = storageRef(firebaseStorage, path);
  const uploadTask = uploadBytesResumable(fileRef, file);

  uploadTask.on(
    "state_changed",
    (snapshot) => {
      progreso.value = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
    },
    (error) => {
      subiendo.value = false;
      errorSubida.value = "Error al subir el archivo. Intentá de nuevo.";
      console.error("Error de subida:", error);
    },
    async () => {
      try {
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
        const nuevoArchivo = {
          nombre: file.name,
          tipo: file.type,
          url: downloadURL,
          storageRef: path,
          fechaSubida: new Date().toISOString(),
        };
        // Esperar confirmación de que Firestore guardó la referencia.
        // Si falla, borrar el archivo de Storage para no dejarlo huérfano.
        await new Promise((resolve) => {
          emit("agregar", nuevoArchivo, async (exito) => {
            if (!exito) {
              try {
                await deleteObject(fileRef);
              } catch (delErr) {
                console.warn("No se pudo limpiar el archivo huérfano de Storage:", delErr);
              }
              errorSubida.value = "No se pudo guardar en la nube. Intentá subir de nuevo.";
            }
            resolve();
          });
        });
      } catch (err) {
        errorSubida.value = "No se pudo obtener la URL del archivo.";
      } finally {
        subiendo.value = false;
        progreso.value = 0;
      }
    }
  );
}

function formatearFecha(isoString) {
  const d = new Date(isoString);
  const dia = String(d.getDate()).padStart(2, '0');
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const anio = d.getFullYear();
  return `${dia}/${mes}/${anio}`;
}

function esImagen(archivo) {
  if (archivo.tipo && archivo.tipo.startsWith('image/')) return true;
  if (archivo.nombre && /\.(jpg|jpeg|png|gif|webp)$/i.test(archivo.nombre)) return true;
  return false;
}

function abrirVisor(archivo) {
  imagenSeleccionada.value = archivo;
  visorAbierto.value = true;
  zoomActivo.value = false;
}

function cerrarVisor() {
  visorAbierto.value = false;
  setTimeout(() => {
    imagenSeleccionada.value = null;
    zoomActivo.value = false;
  }, 200);
}

async function handleZoomClick(event) {
  if (zoomActivo.value) {
    zoomActivo.value = false;
    return;
  }

  // Calcular posición relativa del clic en la imagen
  const img = visorWrapper.value?.querySelector('img');
  if (!img) return;

  let xPercent = 0.5;
  let yPercent = 0.5;

  if (event.target === img) {
    const rect = img.getBoundingClientRect();
    xPercent = (event.clientX - rect.left) / rect.width;
    yPercent = (event.clientY - rect.top) / rect.height;
  }

  zoomActivo.value = true;
  await nextTick();

  // Ajustar scroll para centrar el punto clicado
  if (visorWrapper.value) {
    const imgNew = visorWrapper.value.querySelector('img');
    if (imgNew) {
      visorWrapper.value.scrollLeft = (imgNew.offsetWidth * xPercent) - (visorWrapper.value.clientWidth / 2);
      visorWrapper.value.scrollTop = (imgNew.offsetHeight * yPercent) - (visorWrapper.value.clientHeight / 2);
    }
  }
}
</script>

<style scoped>
.files-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.file-item {
  display: flex;
  align-items: center;
  background: #f8fafc;
  padding: 12px;
  border-radius: 8px;
  gap: 12px;
}
.file-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.file-name {
  color: #111827;
  font-weight: 600;
  font-size: 14px;
}
.file-type {
  color: #64748b;
  font-size: 12px;
}
.file-fecha {
  color: #94a3b8;
  font-size: 11px;
}

.btn-icon-action {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  padding: 6px;
  border-radius: 4px;
  transition: background 0.2s;
  text-decoration: none; /* Para el link de descarga */
  display: flex;
  align-items: center;
  justify-content: center;
}
.btn-icon-action:hover {
  background: #e2e8f0;
}
.btn-icon-action.btn-danger:hover {
  background: #fee2e2;
}

.file-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.actions-internal {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
}

.btn-action {
  background: #eaf1ff;
  color: #678AB2;
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  transition: background 0.2s;
}
.btn-action:hover {
  background: #dbeafe;
}
.btn-action:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.upload-progress {
  height: 5px;
  background: #e2e8f0;
  border-radius: 3px;
  margin-bottom: 10px;
  overflow: hidden;
}
.upload-progress-bar {
  height: 100%;
  background: #678AB2;
  border-radius: 3px;
  transition: width 0.2s;
}
.upload-error {
  color: #dc2626;
  font-size: 13px;
  text-align: center;
  margin-bottom: 8px;
}

.empty-msg {
  color: #9ca3af;
  font-style: italic;
  text-align: center;
  margin: 16px 0;
}

/* Estilos específicos para mejorar el encabezado en la ficha */
:deep(.card) {
  padding: 0 !important;
  overflow: hidden;
}

:deep(.card-header) {
  background-color: #f8f9fa;
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 0 !important;
}

:deep(.card-body) {
  padding: 16px;
}

.file-preview {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 6px;
  cursor: pointer;
  transition: transform 0.2s;
}

.file-preview:hover {
  transform: scale(1.05);
}

.file-icon-placeholder {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  background: #e2e8f0;
  border-radius: 6px;
  color: #64748b;
}

/* Visor de Imágenes */
.visor-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.9);
  z-index: 3000;
  display: flex;
  justify-content: center;
  align-items: center;
}

.visor-container {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.btn-cerrar {
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  font-size: 28px;
  cursor: pointer;
  z-index: 3002;
}

.btn-cerrar:hover {
  background: rgba(255, 255, 255, 0.4);
}

.visor-imagen-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: zoom-in;
}

.visor-imagen-wrapper img {
  max-width: 90%;
  max-height: 85vh;
  object-fit: contain;
  transition: transform 0.3s ease;
}

.visor-imagen-wrapper.zoomed {
  cursor: zoom-out;
  overflow: auto;
  justify-content: flex-start;
  align-items: flex-start;
}

.visor-imagen-wrapper.zoomed img {
  max-width: none;
  max-height: none;
  transform: none;
  margin: auto;
}

.visor-actions {
  position: absolute;
  bottom: 30px;
  z-index: 3002;
}

.btn-descargar-visor {
  background: #678AB2;
  color: white;
  padding: 10px 24px;
  border-radius: 24px;
  text-decoration: none;
  font-size: 14px;
  font-weight: 600;
}
</style>
